// The values the service stores. Named so a comparison reads as a test against a known
// value rather than against a string that happens to match, and so the set is
// discoverable from one place: these are compared in five modules.
export const STATUS = {
  UNDER_REVIEW: 'under_review',
  DRAFT: 'draft',
  PUBLISHED: 'published',
  PARKED: 'parked',
  LOGGED: 'logged',
  REMOVED: 'removed',
};

// The tier a tutor's submission carries. HOF is never compared against — a draft or a
// published row is HOF by definition — but it is named because the set of three is what
// the wire sends, and a reader looking for it should not conclude it does not exist.
export const TIER = {
  HOF: 'HOF',
  STORY: 'STORY',
  NONE: 'NONE',
};

// Not a tier: the sentinel examKey a tutor's free-text exam arrives under, where what
// they typed is the only name the exam has.
export const OTHER_EXAM_KEY = 'OTHER';

// The three destinations a moderator can send a record to, and what each one does to it.
// Ordered as the drawer offers them: the one that publishes first, the two that do not
// after it.
export const TIER_CHOICES = [
  {
    tier: TIER.HOF,
    label: 'Hall of Fame',
    note: 'Builds the card now and moves it to Ready to publish',
    tone: 'ready',
  },
  {
    tier: TIER.STORY,
    label: 'Impact story',
    note: 'A story about a student, not an achievement',
    tone: 'story',
  },
  {
    tier: TIER.NONE,
    label: 'Not for the Hall of Fame',
    note: 'Judged not to belong',
    tone: 'logged',
  },
];

// A queue key is a status, except for the two that split one status by tier and the one
// that joins two statuses.
export const QUEUE_KEYS = {
  NEEDS_DECISION: STATUS.UNDER_REVIEW + ',' + STATUS.PARKED,
  DRAFT: STATUS.DRAFT,
  PUBLISHED: STATUS.PUBLISHED,
  STORIES: STATUS.LOGGED + ':' + TIER.STORY,
  NOT_HOF: STATUS.LOGGED + ':' + TIER.NONE,
  REMOVED: STATUS.REMOVED,
};

// The front of the queue, not everything: the records waiting on a decision. Named once
// because the hook opens on it and the empty state explains it.
export const DEFAULT_QUEUE_KEY = QUEUE_KEYS.NEEDS_DECISION;

// The tiles are the screen's navigation, and one tile is one queue: the words an admin
// reads, plus the filter that clicking it applies. A queue is not the same thing as a
// status — `logged` holds two different kinds of row, and while the tile was keyed on
// status alone they were impossible to tell apart.
//
// `under_review` was missing from the old filter list entirely. It is the status the service
// assigns a submission that arrives with no tier — i.e. every record created from this
// screen — so those rows could not be filtered to at all.
export const QUEUES = [
  // Awaiting review and Parked were two tiles, and a moderator had to watch both to see
  // their work. They are one queue because the drawer stopped caring which it came from:
  // it now decides a tier AND supplies a stuck row's missing fields, so both kinds of row
  // open onto the same controls. The row still carries its own status, and the status chip
  // still names it, so "needs a decision" and "needs a fix" stay legible inside the tile.
  {
    key: QUEUE_KEYS.NEEDS_DECISION,
    label: 'Needs a decision',
    hint: 'no tier decided, or blocked on a fix',
    tone: 'review',
    query: { status: [STATUS.UNDER_REVIEW, STATUS.PARKED] },
  },
  {
    key: QUEUE_KEYS.DRAFT,
    label: 'Ready to publish',
    // Not "card assembled": `draft` means the tier said HOF. A draft row whose assembly
    // deferred has no card at all, and triage creates more of those.
    hint: 'tier says Hall of Fame',
    tone: 'ready',
    query: { status: STATUS.DRAFT },
  },
  {
    key: QUEUE_KEYS.PUBLISHED,
    label: 'Published',
    hint: 'live on the site',
    tone: 'published',
    query: { status: STATUS.PUBLISHED },
  },
  // These two were one "Logged only" tile. Both are on the student's record and neither was
  // ever headed for the public Hall of Fame, but an impact story is a story someone wants to
  // tell, while NONE is a row judged not to belong — different work, different queues. The
  // tutor's tier is the only thing that separates them, and it was stored at ingest and then
  // never read again.
  {
    key: QUEUE_KEYS.STORIES,
    label: 'Impact stories',
    hint: 'a story, not an achievement',
    tone: 'story',
    query: { status: STATUS.LOGGED, tier: TIER.STORY },
  },
  {
    key: QUEUE_KEYS.NOT_HOF,
    label: 'Not for the Hall of Fame',
    hint: 'judged not to belong',
    tone: 'logged',
    query: { status: STATUS.LOGGED, tier: TIER.NONE },
  },
  {
    key: QUEUE_KEYS.REMOVED,
    label: 'Removed',
    hint: 'withdrawn',
    tone: 'removed',
    query: { status: STATUS.REMOVED },
  },
];

// What a row's own status is called. Keyed by the enum the service stores rather than by
// queue key, because a row carries a status and nothing else — the queue it was reached
// through is not on it, and two queues now share one status.
const STATUS_META = {
  [STATUS.UNDER_REVIEW]: { label: 'Awaiting review', tone: 'review' },
  [STATUS.DRAFT]: { label: 'Ready to publish', tone: 'ready' },
  [STATUS.PUBLISHED]: { label: 'Published', tone: 'published' },
  [STATUS.PARKED]: { label: 'Parked', tone: 'parked' },
  [STATUS.LOGGED]: { label: 'Logged only', tone: 'logged' },
  [STATUS.REMOVED]: { label: 'Removed', tone: 'removed' },
};

// An unrecognised status still has to render, so fall back to the raw value.
export const statusLabel = key =>
  (STATUS_META[key] && STATUS_META[key].label) || key || 'Unknown';
export const statusTone = key => (STATUS_META[key] && STATUS_META[key].tone) || null;

// The chip that names a row's tier, or null when the status already implies it.
//
// Only the rows that are not Hall of Fame candidates earn one: their status reads "Logged
// only", which is equally true of an impact story and of a rejected row, so outside the tile
// that filtered to it the row loses the distinction. A draft or a published row is HOF by
// definition, and a second chip there would only repeat the first.
//
// A null tier is deliberately absent: null is not NONE. Nobody has judged the row yet, which
// is what `under_review` says — labelling it "not for the Hall of Fame" would report a
// decision no one made.
const TIER_NOTES = {
  [TIER.STORY]: { label: 'Impact story', tone: 'story' },
  [TIER.NONE]: { label: 'Not for the Hall of Fame', tone: 'logged' },
};

export const tierNote = tier => TIER_NOTES[tier] || null;

// Records table page sizes. 10 by default so the table stays readable without scrolling;
// 50 for scanning a backlog. Paging is server-side (`?limit=`/`?offset=`) rather than a
// browser-side slice, because each record carries its full submission and assembled record
// JSON — fetching every one of them would grow without bound as records accumulate.
// Search is the one exception: see `SEARCH_FETCH_LIMIT`.
export const PAGE_SIZES = [10, 50];
export const DEFAULT_PAGE_SIZE = PAGE_SIZES[0];

// Mirrors webbackend's HOF_PUBLIC_PAGE_BASE: prod is cuemath.com, everything else is
// k8stage. Runtime, not build time -- one static bundle serves every environment.
const PUBLIC_SITE_PROD = 'https://www.cuemath.com';
const PUBLIC_SITE_NON_PROD = 'https://www.k8stage.cuemath.com';

// The page is served at `/hall-of-fame/student/<slug>/`; dropping that segment or the
// trailing slash 404s. Must agree with `tutor_view._PUBLIC_PAGE_PATH` in webbackend.
export const publicPageUrl = slug => {
  const isProd =
    typeof window !== 'undefined' && window.location.hostname === 'leap-admin.cuemath.com';

  return `${isProd ? PUBLIC_SITE_PROD : PUBLIC_SITE_NON_PROD}/hall-of-fame/student/${slug}/`;
};

// Whitelisted editable record fields (subset the FE exposes).
export const EDITABLE_FIELDS = ['name', 'score', 'category', 'catLabel', 'storyUrl'];

// Saving the first `storyUrl` on an Impact Story notifies the family in chat, so it is
// hidden elsewhere rather than quietly ignored. Shared by the panel that renders the
// fields and the hook that submits them: a field never shown is never sent.
export const editableFieldsFor = tier =>
  EDITABLE_FIELDS.filter(f => f !== 'storyUrl' || tier === TIER.STORY);

// The one blocker code the drawer can do something about — and the one every parked row
// on prod carries. `blockers.py` owns the full list.
export const SCORE_TYPE_DISPUTED = 'SCORE_TYPE_DISPUTED';

// Which typed field each scoring type reads, mirroring `value_rules._VALUE_FIELD` in
// webbackend. The drawer has to know this before it can ask for a value: settling the type
// alone leaves `normalise_value` falling back to whatever the tutor put in `score`, so a
// letter-graded exam would publish "achieved 99".
export const VALUE_FIELD_FOR_SCORE_TYPE = {
  score: 'score',
  percentile: 'score',
  rank: 'score',
  level: 'level',
  award: 'level',
  grade: 'letterGrade',
  admission: 'admitted',
  text: 'otherText',
};

// What the moderator reads. `letterGrade` is not a word, and the wire's `score` is the
// drawer's "result".
// Only the fields that read as something other than "Result", which is the caller's
// fallback — `score` and `otherText` are already it.
export const VALUE_FIELD_LABELS = {
  level: 'Level or award',
  letterGrade: 'Letter grade',
  admitted: 'Admitted',
};

export const VALUE_FIELD_HINTS = {
  level: 'The rung this exam awards. Case and spacing do not have to match the library.',
  letterGrade: 'The grade the school awarded — A+, B-, and so on.',
  admitted: '“yes” if the student was admitted.',
};

// `catLabel`/`category` are near-identical raw keys; spell out which one the
// parent actually sees on the public card.
export const FIELD_LABELS = {
  name: 'Student name — shown on the public card',
  score: 'Score — shown on the public card',
  category: 'Category key (internal grouping)',
  catLabel: 'Category label — shown on the public card',
  storyUrl: 'Impact story link — saving notifies the parent and tutor in chat',
};

// A card normally takes its category from the library entry, and only a custom exam
// arrives with both blank — the shared OTHER entry carries none. Nothing validates what
// gets typed here, so an invented word does not fail, it quietly opens a grouping of one:
// prod already carries an `assessment` holding a single exam. Naming the six that exist is
// the whole guardrail.
const CARD_CATEGORIES = {
  admissions: 'Admissions',
  competition: 'Competition',
  state: 'State Test',
  national: 'National Exam',
  ap: 'AP Exam',
  gifted: 'Gifted & Talented',
};

export const FIELD_HINTS = {
  name: 'First name and last initial. A child’s name on a public page — no full surname.',
  score: 'What the card states, on its own: “9”, “97”, “Prize”. Not a sentence.',
  category: `Lowercase, and one of the six already in use — ${Object.keys(
    CARD_CATEGORIES,
  ).join(', ')}. Nothing checks it, so a new word here makes a grouping of one.`,
  catLabel: `What the parent reads, paired with the key above — ${Object.values(
    CARD_CATEGORIES,
  ).join(', ')}.`,
  storyUrl:
    'The published post on cuemath.com/blog. Saving the first link messages the parent ' +
    'and the tutor with it — correcting it afterwards does not message them again.',
};

// Dropdown reads { id, label, value } and hands onChange the value alone.
export const PAGE_SIZE_OPTIONS = PAGE_SIZES.map(n => ({
  id: String(n),
  label: String(n),
  value: String(n),
}));

// Search filters in the browser: the name it matches on is resolved after the page is cut,
// so the service cannot search by it. 1000 is the service's own `student_names._MAX_LOOKUP`
// — past it rows come back unnamed, so a name search stops matching rather than truncating.
export const SEARCH_FETCH_LIMIT = 1000;
